const { parse, startOfDay, startOfTomorrow } = require("date-fns");

const DATETIME_FORMATS = ["dd.MM.yyyy HH:mm", "dd.MM.yyyy, HH:mm", "dd.MM.yyyy"];

const unwrapDateValue = value => {
  if (!value || typeof value !== "object" || value instanceof Date) return value;
  if (Object.prototype.hasOwnProperty.call(value, "value")) return value.value;
  if (Object.prototype.hasOwnProperty.call(value, "field")) return value.field;

  return value;
};

const hasDateWatchValue = value => {
  const unwrappedValue = unwrapDateValue(value);

  return unwrappedValue !== undefined && unwrappedValue !== null && unwrappedValue !== "";
};

const buildDateWatchRequestParams = (parentWatch, values = {}) => {
  if (!parentWatch?.parent) return {};

  return [parentWatch.parent, ...(parentWatch.dependencies || [])].reduce((params, fieldName) => {
    params[fieldName] = unwrapDateValue(values[fieldName]);
    return params;
  }, {});
};

const dateWatchRequestIsReady = (parentWatch, values = {}) => {
  if (!parentWatch?.parent || !parentWatch?.parent_params || Object.keys(parentWatch.parent_params).length === 0) {
    return false;
  }

  const requiredFieldNames = [parentWatch.parent, ...(parentWatch.dependencies || [])];
  return requiredFieldNames.every(fieldName => hasDateWatchValue(values[fieldName]));
};

const parseDateConditionValue = value => {
  const unwrappedValue = unwrapDateValue(value);

  if (unwrappedValue instanceof Date) {
    return Number.isNaN(unwrappedValue.getTime()) ? null : unwrappedValue;
  }

  if (typeof unwrappedValue === "number") {
    const date = new Date(unwrappedValue);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof unwrappedValue !== "string" || unwrappedValue.trim() === "") return null;

  const stringValue = unwrappedValue.trim();

  for (const dateFormat of DATETIME_FORMATS) {
    const parsedDate = parse(stringValue, dateFormat, new Date());

    if (!Number.isNaN(parsedDate.getTime())) return parsedDate;
  }

  const nativeDate = new Date(stringValue);
  return Number.isNaN(nativeDate.getTime()) ? null : nativeDate;
};

const resolveConditionDate = ({ condition, values, now }) => {
  if (condition.condition_field === "current_datetime") {
    const currentDate = new Date(now);
    currentDate.setMinutes(currentDate.getMinutes() - 5);
    return currentDate;
  }

  if (condition.condition_field === "start_of_tomorrow") return startOfTomorrow();

  const fieldName = condition.condition_value_field_name || condition.condition_field;
  return parseDateConditionValue(values?.[fieldName]);
};

const compareDates = (currentDate, conditionDate, operator) => {
  const currentTimestamp = currentDate.getTime();
  const conditionTimestamp = conditionDate.getTime();

  if (operator === "<") return currentTimestamp < conditionTimestamp;
  if (operator === ">") return currentTimestamp > conditionTimestamp;
  if (operator === "<=") return currentTimestamp <= conditionTimestamp;
  if (operator === ">=") return currentTimestamp >= conditionTimestamp;
  if (["=", "==", "==="].includes(operator)) return currentTimestamp === conditionTimestamp;

  return false;
};

const validateDateConditions = ({ value, watch, values = {}, now = new Date() }) => {
  const conditions = Array.isArray(watch?.conditions) ? watch.conditions : [];
  const parsedValue = parseDateConditionValue(value);

  if (conditions.length === 0) return { valid: true, condition: null };
  if (!parsedValue) return { valid: false, condition: conditions[0] };

  for (const condition of conditions) {
    if (!condition || Object.keys(condition).length === 0) continue;

    const conditionDate = resolveConditionDate({ condition, values, now });

    // The legacy field skips a condition until its related form value exists.
    if (!condition.expect_result || !conditionDate) continue;

    const compareCurrentDate = condition.condition_field_option === "only_date" ? startOfDay(parsedValue) : parsedValue;
    const compareConditionDate =
      condition.condition_field_option === "only_date" ? startOfDay(conditionDate) : conditionDate;

    if (!compareDates(compareCurrentDate, compareConditionDate, condition.expect_result)) {
      return { valid: false, condition };
    }
  }

  return { valid: true, condition: null };
};

module.exports = {
  buildDateWatchRequestParams,
  dateWatchRequestIsReady,
  parseDateConditionValue,
  validateDateConditions,
};
